import { Injectable } from '@angular/core';
import { LocalStorageService } from '../../cache';
import { CompanyClassificationType } from '../../constants-types';
import { Company } from '../../core-data-model';
import { InitDataService } from '../../system-services';
import { HashService } from '../HashService';

type LocalStorageDemoCompanyValue = {
    companies: CompanyValue[];
    expiration: Date;
};

type CompanyValue = {
    id: string;
    name: string;
    companyClassificationType: CompanyClassificationType;
    deleted: boolean;
};

@Injectable({
    providedIn: 'root'
})
export class LocalDemoAccountStoreService {
    constructor(
        private initData: InitDataService,
        private hashService: HashService,
        private localStorage: LocalStorageService
    ) {}

    // This is needed to be able to help bypass the cache that's in place for the company selector
    // This ensures that the company will always exist in the selector and in admin
    async addCompanyToLocalStorage(
        companyGuid: string,
        companyName: string,
        companyClassificationType: CompanyClassificationType,
        deleted = false
    ) {
        const currentUserEmail = this.initData.user.email.toLowerCase();
        const hashedEmail =
            await this.hashService.computeHashHex(currentUserEmail);
        const storageKey = `${hashedEmail}-demoCompanies`;
        const storedItem = this.localStorage.getItem<string>(storageKey);
        const storedData: LocalStorageDemoCompanyValue = storedItem
            ? JSON.parse(storedItem)
            : { companies: [] };

        // Expire the local storage an hour from now
        const expiration = new Date();
        expiration.setHours(expiration.getHours() + 1);

        let company = storedData.companies.find((x) => x.id === companyGuid);

        if (company) {
            company.id = companyGuid;
            company.name = companyName;
            company.companyClassificationType = companyClassificationType;
            company.deleted = deleted;
        } else {
            company = {
                id: companyGuid,
                name: companyName,
                companyClassificationType: companyClassificationType,
                deleted: deleted
            };
            storedData.companies.push(company);
        }

        storedData.expiration = expiration;

        this.localStorage.setItem(storageKey, JSON.stringify(storedData));
    }

    async getLocalStorageCompanies(): Promise<Company[]> {
        const currentUserEmail = this.initData.user.email.toLowerCase();
        const hashedEmail =
            await this.hashService.computeHashHex(currentUserEmail);
        const storageKey = `${hashedEmail}-demoCompanies`;
        const storedData = this.localStorage.getItem<string>(storageKey);

        if (!storedData) {
            return [];
        }

        const formattedStoredData: LocalStorageDemoCompanyValue =
            JSON.parse(storedData);

        const now = new Date();
        const expiration = new Date(formattedStoredData.expiration);

        if (expiration < now) {
            this.localStorage.removeItem(storageKey);
            return [];
        }

        return formattedStoredData.companies.map<Company>((company) => ({
            name: company.name,
            id: company.id,
            shortPublicId: '',
            isTrialAccount: false,
            isDefault: false,
            isRetired: false,
            isDeleted: company.deleted,
            isEnterprise: false,
            hasCognitiveAccess: true,
            allowMultipleOILinks: false,
            languageLocale: '',
            msTimeZoneIdentifier: '',
            maxNumberOfOILinks: 0,
            companyClassificationType: company.companyClassificationType
        }));
    }
}
